import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Loader2, HeartHandshake, Check, X, Star, StarOff,
  Award, Pencil, Trash2, RefreshCw, ExternalLink, Mail,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";

const STATUS_TABS = [
  { key: "pending",  label: "Pending" },
  { key: "approved", label: "Approved" },
  { key: "all",      label: "All" },
];

const ANIMAL_OPTIONS = [
  { value: "dogs",  label: "Dogs" },
  { value: "cats",  label: "Cats" },
  { value: "both",  label: "Both" },
  { value: "other", label: "Other" },
];

function Badge({ children, tone = "zinc", testid }) {
  const tones = {
    zinc:  "border-zinc-300 bg-zinc-50 text-zinc-700",
    red:   "border-red-700 bg-red-50 text-red-700",
    amber: "border-amber-500 bg-amber-50 text-amber-800",
    green: "border-green-700 bg-green-50 text-green-800",
    gold:  "border-yellow-700 bg-yellow-50 text-yellow-900",
  };
  return (
    <span
      data-testid={testid}
      className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 border ${tones[tone] || tones.zinc}`}
    >
      {children}
    </span>
  );
}

export default function AdminRescuePartnersList({ token }) {
  const headers = useMemo(() => ({ "X-Admin-Token": token }), [token]);
  const [status, setStatus] = useState("pending");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [editing, setEditing] = useState(null); // rescue doc or null

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const params = status === "all" ? {} : { status };
      const { data } = await api.get("/admin/rescue-partners", { headers, params });
      setItems(data.items || []);
    } catch {
      toast.error("Failed to load rescue partners.");
    } finally {
      setLoading(false);
    }
  }, [token, status, headers]);

  useEffect(() => { load(); }, [load]);

  const approve = async (r) => {
    setBusyId(r.id);
    try {
      await api.post(`/admin/rescue-partners/${r.id}/approve`, {}, { headers });
      toast.success(`Approved ${r.name}.`);
      load();
    } catch {
      toast.error("Approve failed.");
    } finally {
      setBusyId(null);
    }
  };

  const reject = async (r) => {
    if (!window.confirm(`Reject and delete "${r.name}"?`)) return;
    setBusyId(r.id);
    try {
      await api.delete(`/admin/rescue-partners/${r.id}`, { headers });
      toast.success(`Deleted ${r.name}.`);
      load();
    } catch {
      toast.error("Delete failed.");
    } finally {
      setBusyId(null);
    }
  };

  const toggleFeatured = async (r) => {
    setBusyId(r.id);
    try {
      await api.post(`/admin/rescue-partners/${r.id}/toggle-featured`, {}, { headers });
      toast.success(r.is_featured ? "Removed from featured strip." : "Added to featured strip.");
      load();
    } catch {
      toast.error("Toggle featured failed.");
    } finally {
      setBusyId(null);
    }
  };

  const toggleFounding = async (r) => {
    if (!r.is_founding_partner && !r.is_approved) {
      toast.error("Approve the rescue first before granting Founding Partner.");
      return;
    }
    const label = r.is_founding_partner
      ? `Remove the Founding Rescue Partner designation from "${r.name}"?`
      : `Grant "${r.name}" the Founding Rescue Partner designation? This will show a gold badge on the public page.`;
    if (!window.confirm(label)) return;
    setBusyId(r.id);
    try {
      const { data } = await api.post(
        `/admin/rescue-partners/${r.id}/toggle-founding`,
        {}, { headers },
      );
      toast.success(data.is_founding_partner ? "Marked as Founding Rescue Partner." : "Founding designation removed.");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Founding toggle failed.");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="border border-zinc-900 bg-white" data-testid="admin-rescue-partners">
      <div className="px-5 py-3 border-b border-zinc-900 flex items-center gap-3 flex-wrap justify-between">
        <div className="flex items-center gap-2">
          <HeartHandshake size={14} className="text-red-600" />
          <span className="label-eyebrow">Rescue Partners</span>
          <span className="text-xs text-zinc-500 font-mono">({items.length})</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex border border-zinc-300">
            {STATUS_TABS.map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => setStatus(t.key)}
                data-testid={`admin-rescue-tab-${t.key}`}
                className={`px-3 py-1 text-[10px] uppercase tracking-widest font-bold ${
                  status === t.key ? "bg-zinc-900 text-white" : "bg-white text-zinc-800 hover:bg-zinc-100"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={load}
            className="btn-outline text-xs inline-flex items-center gap-1"
            data-testid="admin-rescue-refresh"
          >
            <RefreshCw size={12} /> Refresh
          </button>
        </div>
      </div>

      <div className="divide-y divide-zinc-200">
        {loading && (
          <div className="p-6 text-sm text-zinc-500 inline-flex items-center gap-2">
            <Loader2 size={14} className="animate-spin" /> Loading…
          </div>
        )}
        {!loading && items.length === 0 && (
          <div className="p-6 text-sm text-zinc-500">No rescue partners in this view.</div>
        )}
        {!loading && items.map((r) => (
          <div key={r.id} className="p-5 flex flex-col md:flex-row md:items-start gap-4" data-testid={`admin-rescue-row-${r.id}`}>
            <div className="flex-shrink-0 w-14 h-14 border border-zinc-300 bg-zinc-50 flex items-center justify-center overflow-hidden">
              {r.logo_url ? (
                <img src={r.logo_url} alt="" className="w-full h-full object-contain" onError={(e) => { e.currentTarget.style.display = "none"; }} />
              ) : (
                <HeartHandshake size={22} className="text-zinc-400" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center flex-wrap gap-2">
                <h3 className="font-display font-black text-lg leading-tight">{r.name}</h3>
                {r.is_approved
                  ? <Badge tone="green" testid={`admin-rescue-status-${r.id}`}><Check size={10}/> Approved</Badge>
                  : <Badge tone="amber" testid={`admin-rescue-status-${r.id}`}>Pending</Badge>}
                {r.is_featured && <Badge tone="red"><Star size={10}/> Featured</Badge>}
                {r.is_founding_partner && <Badge tone="gold" testid={`admin-rescue-founding-badge-${r.id}`}><Award size={10}/> Founding</Badge>}
              </div>
              <div className="text-xs text-zinc-600 mt-1 font-mono break-all">
                {r.city}, {r.state_code || r.state} · {r.animals}
                {r.animals === "other" && r.animals_other ? ` (${r.animals_other})` : ""}
              </div>
              <div className="text-xs text-zinc-500 mt-1 flex flex-wrap items-center gap-x-4 gap-y-1">
                {r.contact_email && (
                  <span className="inline-flex items-center gap-1"><Mail size={11}/> {r.contact_name || "—"} · {r.contact_email}</span>
                )}
                {r.website && (
                  <a href={r.website} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-red-700 hover:underline">
                    <ExternalLink size={11}/> {r.website.replace(/^https?:\/\//, "").split("/")[0]}
                  </a>
                )}
              </div>
              <p className="text-sm text-zinc-700 mt-2 line-clamp-3">{r.description}</p>
            </div>

            <div className="flex flex-col gap-2 md:min-w-[220px]">
              {!r.is_approved && (
                <button
                  type="button"
                  disabled={busyId === r.id}
                  onClick={() => approve(r)}
                  data-testid={`admin-rescue-approve-${r.id}`}
                  className="inline-flex items-center justify-center gap-1 bg-green-700 text-white text-xs font-bold uppercase tracking-widest px-3 py-2 hover:bg-green-800 disabled:opacity-60"
                >
                  {busyId === r.id ? <Loader2 size={12} className="animate-spin"/> : <Check size={12}/>} Approve
                </button>
              )}
              {r.is_approved && (
                <button
                  type="button"
                  disabled={busyId === r.id}
                  onClick={() => toggleFounding(r)}
                  data-testid={`admin-rescue-founding-toggle-${r.id}`}
                  className={`inline-flex items-center justify-center gap-1 text-xs font-bold uppercase tracking-widest px-3 py-2 border disabled:opacity-60 ${
                    r.is_founding_partner
                      ? "bg-yellow-50 text-yellow-900 border-yellow-700 hover:bg-yellow-100"
                      : "bg-white text-zinc-900 border-zinc-900 hover:bg-yellow-50"
                  }`}
                >
                  <Award size={12}/> {r.is_founding_partner ? "Remove Founding" : "Mark Founding"}
                </button>
              )}
              {r.is_approved && (
                <button
                  type="button"
                  disabled={busyId === r.id}
                  onClick={() => toggleFeatured(r)}
                  data-testid={`admin-rescue-featured-${r.id}`}
                  className="inline-flex items-center justify-center gap-1 btn-outline text-xs disabled:opacity-60"
                >
                  {r.is_featured ? <StarOff size={12}/> : <Star size={12}/>}
                  {r.is_featured ? "Unfeature" : "Feature on homepage"}
                </button>
              )}
              <button
                type="button"
                onClick={() => setEditing(r)}
                data-testid={`admin-rescue-edit-${r.id}`}
                className="inline-flex items-center justify-center gap-1 btn-outline text-xs"
              >
                <Pencil size={12}/> Edit
              </button>
              <button
                type="button"
                disabled={busyId === r.id}
                onClick={() => reject(r)}
                data-testid={`admin-rescue-delete-${r.id}`}
                className="inline-flex items-center justify-center gap-1 text-xs font-bold uppercase tracking-widest px-3 py-2 text-red-700 border border-red-700 hover:bg-red-50 disabled:opacity-60"
              >
                <Trash2 size={12}/> {r.is_approved ? "Delete" : "Reject"}
              </button>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <RescueEditModal
          rescue={editing}
          headers={headers}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function RescueEditModal({ rescue, headers, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: rescue.name || "",
    city: rescue.city || "",
    state: rescue.state || "",
    state_code: rescue.state_code || "",
    website: rescue.website || "",
    social_facebook: rescue.social_facebook || "",
    social_instagram: rescue.social_instagram || "",
    social_tiktok: rescue.social_tiktok || "",
    social_x: rescue.social_x || "",
    animals: rescue.animals || "both",
    animals_other: rescue.animals_other || "",
    description: rescue.description || "",
    donate_url: rescue.donate_url || "",
    logo_url: rescue.logo_url || "",
  });
  const [saving, setSaving] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put(`/admin/rescue-partners/${rescue.id}`, form, { headers });
      toast.success("Saved.");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[10000] bg-black/70 flex items-center justify-center p-4" onClick={onClose} data-testid="admin-rescue-edit-modal">
      <div className="bg-white border-2 border-zinc-900 max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="bg-zinc-900 text-white px-5 py-4 flex items-center justify-between border-b border-red-600">
          <div>
            <div className="label-eyebrow text-red-500">Edit Rescue Partner</div>
            <div className="font-display font-black text-xl mt-1">{rescue.name}</div>
          </div>
          <button type="button" onClick={onClose} className="text-white hover:text-red-500 text-xl" aria-label="Close">×</button>
        </div>
        <form onSubmit={save} className="p-5 space-y-4 text-sm">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><label className="label-eyebrow block mb-1">Name</label><input className="field" value={form.name} onChange={set("name")} required data-testid="admin-rescue-edit-name"/></div>
            <div><label className="label-eyebrow block mb-1">Animals</label>
              <select className="field" value={form.animals} onChange={set("animals")}>
                {ANIMAL_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div><label className="label-eyebrow block mb-1">City</label><input className="field" value={form.city} onChange={set("city")}/></div>
            <div><label className="label-eyebrow block mb-1">State</label><input className="field" value={form.state} onChange={set("state")}/></div>
            <div><label className="label-eyebrow block mb-1">State code</label><input className="field" value={form.state_code} onChange={set("state_code")} maxLength={2}/></div>
            <div><label className="label-eyebrow block mb-1">Website</label><input className="field" value={form.website} onChange={set("website")}/></div>
            <div><label className="label-eyebrow block mb-1">Facebook</label><input className="field" value={form.social_facebook} onChange={set("social_facebook")}/></div>
            <div><label className="label-eyebrow block mb-1">Instagram</label><input className="field" value={form.social_instagram} onChange={set("social_instagram")}/></div>
            <div><label className="label-eyebrow block mb-1">TikTok</label><input className="field" value={form.social_tiktok} onChange={set("social_tiktok")}/></div>
            <div><label className="label-eyebrow block mb-1">X (Twitter)</label><input className="field" value={form.social_x} onChange={set("social_x")}/></div>
            <div className="sm:col-span-2"><label className="label-eyebrow block mb-1">Donate URL</label><input className="field" value={form.donate_url} onChange={set("donate_url")}/></div>
            <div className="sm:col-span-2"><label className="label-eyebrow block mb-1">Logo URL</label><input className="field" value={form.logo_url} onChange={set("logo_url")}/></div>
            {form.animals === "other" && (
              <div className="sm:col-span-2"><label className="label-eyebrow block mb-1">Other animals (free text)</label><input className="field" value={form.animals_other} onChange={set("animals_other")}/></div>
            )}
            <div className="sm:col-span-2"><label className="label-eyebrow block mb-1">Description</label><textarea rows={4} className="field" value={form.description} onChange={set("description")}/></div>
          </div>
          <div className="flex items-center justify-end gap-2 pt-2 border-t border-zinc-200">
            <button type="button" onClick={onClose} className="btn-outline text-xs">Cancel</button>
            <button type="submit" disabled={saving} className="btn-primary text-xs disabled:opacity-60" data-testid="admin-rescue-edit-save">
              {saving ? <Loader2 size={12} className="animate-spin"/> : <Check size={12}/>} Save
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
